import { ComponentMultiStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';
import { mode } from '@chakra-ui/theme-tools';

import { COLORS } from '../../theme';

import { TabsProps } from './Tabs';

const enclosedFontSizeMap = {
  sm: 'sm',
  md: 'md',
  lg: 'lg'
};
const enclosedBorderRadiusMap = {
  sm: '12px',
  md: '16px',
  lg: '20px'
};
const enclosedTabHeightMap = {
  sm: '24px',
  md: '32px',
  lg: '40px'
};

const focusVisibleStyles = {
  boxShadow: `0px 0px 0px 4px ${COLORS.blue.lighter}`
};

const TabsTheme: ComponentMultiStyleConfig = {
  parts: ['root', 'tab', 'tablist', 'tabpanel'],
  baseStyle: {
    tab: {
      fontWeight: 'normal',
      background: 'transparent',
      ':not(:focus-visible)': {
        boxShadow: 'none',
        outline: 'none'
      },
      transitionDuration: '0s'
    },
    tabpanel: {
      padding: '24px'
    }
  },
  sizes: {
    sm: {
      tab: {
        height: '32px',
        fontSize: 'md',
        padding: '0px 16px'
      },
      tablist: {
        height: '32px'
      }
    },
    md: {
      tab: {
        height: '40px',
        fontSize: 'lg',
        padding: '0px 24px'
      },
      tablist: {
        height: '40px'
      }
    },
    lg: {
      tab: {
        height: '48px',
        fontSize: 'xl',
        padding: '0px 32px'
      },
      tablist: {
        height: '48px'
      }
    }
  },
  variants: {
    line: (props: StyleFunctionProps) => ({
      tab: {
        color: mode('grey.light', 'grey.lighter')(props),
        borderBottom: '1px',
        _selected: {
          color: mode('black.lighter', 'silver.lighter')(props),
          fontWeight: 'medium',
          borderBottom: '1px'
        },
        _active: {
          color: mode('black.lighter', 'silver.lighter')(props),
          background: 'none'
        },
        _hover: {
          color: mode('black.lighter', 'silver.lighter')(props)
        },
        _focusVisible: focusVisibleStyles
      },
      tablist: {
        borderBottom: '1px solid',
        borderColor: props.borderColor ? props.borderColor : mode('silver.base', 'grey.base')(props)
      }
    }),
    enclosed: (props: TabsProps & StyleFunctionProps) => ({
      tab: {
        color: mode('grey.light', 'grey.light')(props),
        fontSize: enclosedFontSizeMap[props.size],
        height: enclosedTabHeightMap[props.size],
        borderRadius: enclosedBorderRadiusMap[props.size],
        _selected: {
          color: 'black.lighter',
          background: mode('silver.lightest', 'grey.lightest')(props),
          border: '0',
          boxShadow:
            '0px 2px 4px -2px rgba(18, 21, 26, 0.08), 0px 2px 2px 0px rgba(18, 21, 26, 0.04), 0px 0px 0px 0.5px rgba(18, 21, 26, 0.08)',
          fontWeight: 'medium'
        },
        _hover: {
          color: mode('black.lighter', 'silver.lighter')(props),
          ':not([aria-selected="false"])': {
            color: mode('black.lighter', 'black.lighter')(props)
          }
        },
        _focusVisible: focusVisibleStyles
      },
      tablist: {
        alignItems: 'flex-start',
        background: mode('silver.light', 'black.lighter')(props),
        border: '0',
        borderRadius: '40px',
        padding: '4px'
      }
    })
  },
  defaultProps: {
    colorScheme: undefined
  }
};

export default TabsTheme;
